/* eslint-disable react/jsx-no-bind, react/jsx-props-no-spreading */
import {
  Search,
  Refresh,
  Visibility,
  Edit,
  Delete,
  Add,
  Close,
  Info,
  CloudUpload,
} from '@mui/icons-material';
import BrokenImageIcon from '@mui/icons-material/BrokenImage';
import CheckCircle from '@mui/icons-material/CheckCircle';
import RemoveCircle from '@mui/icons-material/RemoveCircle';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import {
  Box,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem as DropdownItem,
  Paper,
  IconButton,
  Menu,
  CardContent,
  Card,
  TablePagination,
  Modal,
  Typography,
  useTheme,
  Divider,
  Grid,
  FormControl,
  Select,
  FormHelperText,
  Switch,
  FormControlLabel,
  Chip,
  Tooltip,
  Radio,
  RadioGroup,
  InputAdornment,
  Avatar,
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { toast } from 'react-toastify';

import { tokens } from '../../theme';
import { formatToIST, formatToISTCustom } from '../../utils/dateFormatter';
import logger from '../../utils/logger';

import promotionService from './promotionService';

import './index.scss';

// Extend dayjs with plugins
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

// Helper: Convert various date formats to dayjs or null
const toDayjsOrNull = (value) => {
  if (value == null || value === '') return null;
  if (dayjs.isDayjs(value) && value.isValid()) return value;
  const d = dayjs(value);
  if (d.isValid()) return d;
  // Try DD-MM-YYYY or DD-MM-YYYY HH:mm:ss (API may return this)
  const d2 = dayjs(value, 'DD-MM-YYYY HH:mm:ss', true);
  if (d2.isValid()) return d2;
  const d3 = dayjs(value, 'DD-MM-YYYY', true);
  return d3.isValid() ? d3 : null;
};

const Promotions = () => {
  const [, setImageError] = useState(false);
  const [apiResponse, setApiResponse] = useState({
    status: false,
    message: '',
    data: {
      promotions: [],
      pagination: { total: 0, page: 1, limit: 10, totalPages: 1 },
    },
    status_code: 0,
  });
  const [menuAnchor, setMenuAnchor] = useState(null);
  // MUI TablePagination uses 0-based page index; API uses 1-based
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [modalOpen, setModalOpen] = useState(false);
  const [createPromotionModalOpen, setCreatePromotionModalOpen] =
    useState(false);
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [previewImageSrc, setPreviewImageSrc] = useState('');
  const handleImagePreviewOpen = (src) => {
    if (!src) return;
    setPreviewImageSrc(src);
    setImagePreviewOpen(true);
  };

  const handleImagePreviewClose = () => {
    setImagePreviewOpen(false);
    setPreviewImageSrc('');
  };

  const [newPromotion, setNewPromotion] = useState({
    title: '',
    code: '',
    type: '',
    discountValue: 0,
    minOrderValue: 0,
    usageLimit: 0,
    startDate: '',
    endDate: '',
    active: true,
  });
  const [selectedPromotion, setSelectedPromotion] = useState({
    title: '',
    code: '',
    type: '',
    description: '',
    discountType: 'p',
    discountValue: '',
    maxDiscount: '',
    minOrderValue: '',
    startDate: null,
    endDate: null,
    usageLimit: '',
    dailyLimit: '',
    userMaxLimit: '',
    targetedUserIds: '',
    image: null,
    active: true,
  });
  const [isEditMode, setIsEditMode] = useState(false);
  const [errors, setErrors] = useState({
    title: '',
    code: '',
    type: '',
    discountValue: '',
    minOrderValue: '',
    usageLimit: '',
    startDate: '',
    endDate: '',
  });

  const [loading, setLoading] = useState(false);
  const isWidgetView = true;
  const [isDragging, setIsDragging] = useState(false);

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  // React Hook Form for filter form
  const {
    control: filterControl,
    watch: watchFilter,
    reset: resetFilter,
    formState: { errors: formErrors },
  } = useForm({
    defaultValues: {
      search: '',
      promotionId: '',
      typeFilter: 'All Types',
      activeStatus: null,
      startDate: null,
      endDate: null,
    },
    mode: 'onChange',
  });

  // React Hook Form for create promotion form
  const {
    control: createPromotionControl,
    handleSubmit: handleCreatePromotionSubmit,
    watch: watchCreatePromotion,
    reset: resetCreatePromotion,
    setValue: setCreatePromotionValue,
    formState: { errors: createPromotionFormErrors },
  } = useForm({
    defaultValues: {
      title: '',
      code: '',
      type: '',
      description: '',
      discountType: 'p',
      discountValue: '',
      maxDiscount: '',
      minOrderValue: '',
      startDate: null,
      endDate: null,
      usageLimit: '',
      dailyLimit: '',
      userMaxLimit: '',
      targetedUserIds: '',
      image: null,
    },
    mode: 'onChange',
  });

  // Watch form values for filtering (current input values)
  const searchInput = watchFilter('search');
  const promotionIdInput = watchFilter('promotionId');
  const typeFilterInput = watchFilter('typeFilter');
  const activeStatusInput = watchFilter('activeStatus');
  const startDateInput = watchFilter('startDate');
  const endDateInput = watchFilter('endDate');

  // Applied filter values (used for API calls)
  const [appliedSearch, setAppliedSearch] = useState('');
  const [appliedPromotionId, setAppliedPromotionId] = useState('');
  const [appliedTypeFilter, setAppliedTypeFilter] = useState('All Types');
  const [appliedActiveStatus, setAppliedActiveStatus] = useState(null);
  const [appliedStartDate, setAppliedStartDate] = useState(null);
  const [appliedEndDate, setAppliedEndDate] = useState(null);

  // Watch discount type for conditional rendering
  const discountType = watchCreatePromotion('discountType');
  const isPercentageDiscount =
    discountType === 'p' || discountType === 'percentage';
  // Watch promotion type for conditional rendering
  const promotionType = watchCreatePromotion('type');
  const promotionDescription = watchCreatePromotion('description');

  // Watch required fields to check if form is valid
  const createTitle = watchCreatePromotion('title');
  const createCode = watchCreatePromotion('code');
  const createType = watchCreatePromotion('type');
  const createDiscountValue = watchCreatePromotion('discountValue');
  const createMinOrderValue = watchCreatePromotion('minOrderValue');
  const createStartDate = watchCreatePromotion('startDate');
  const createEndDate = watchCreatePromotion('endDate');
  const createImageCtrl = watchCreatePromotion('image');
  const createTargetingUsers = watchCreatePromotion('targetedUserIds');

  // Check if all required fields are valid
  const areRequiredFieldsValid =
    createTitle &&
    createTitle.trim().length >= 3 &&
    createCode &&
    createCode.trim().length >= 3 &&
    /^[A-Z0-9]+$/.test(createCode) &&
    promotionDescription !== '' &&
    createType &&
    createType.trim() !== '' &&
    discountType &&
    createDiscountValue !== '' &&
    createDiscountValue != null &&
    Number(createDiscountValue) > 0 &&
    (!isPercentageDiscount || Number(createDiscountValue) <= 100) &&
    createMinOrderValue !== '' &&
    createMinOrderValue != null &&
    Number(createMinOrderValue) >= 0 &&
    createStartDate &&
    createEndDate &&
    createImageCtrl !== null &&
    (createType !== 'SPU' ||
      (createTargetingUsers && createTargetingUsers !== '')) &&
    dayjs(createStartDate).isValid() &&
    dayjs(createEndDate).isValid() &&
    dayjs(createStartDate).isBefore(dayjs(createEndDate)) &&
    !dayjs(createEndDate).isBefore(dayjs(), 'day') &&
    !createPromotionFormErrors?.title &&
    !createPromotionFormErrors?.code &&
    !createPromotionFormErrors?.type &&
    !createPromotionFormErrors?.description &&
    !createPromotionFormErrors?.discountValue &&
    !createPromotionFormErrors?.minOrderValue &&
    !createPromotionFormErrors?.startDate &&
    !createPromotionFormErrors?.endDate &&
    !createPromotionFormErrors?.targetedUserIds &&
    !createPromotionFormErrors?.image;

  useEffect(() => {
    if (!isPercentageDiscount) {
      setCreatePromotionValue('maxDiscount', '');
    }
  }, [isPercentageDiscount, setCreatePromotionValue]);

  const promotionData = apiResponse.data?.promotions || [];
  const pagination = apiResponse.data?.pagination || {
    total: 0,
    page: page + 1,
    limit: rowsPerPage,
    totalPages: 1,
  };

  useEffect(() => {
    // Initial fetch on mount and when page/rowsPerPage changes
    // Filters are applied only when "Search" button is clicked
    fetchPromotions(
      page,
      rowsPerPage,
      appliedSearch,
      appliedTypeFilter,
      appliedActiveStatus,
      appliedStartDate,
      appliedEndDate,
      appliedPromotionId,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, selectedPromotion]);

  const handleSearchChange = (e) => {
    // Handled by React Hook Form watch
  };

  const handleApplyFilters = () => {
    // Update applied filter values from current input values
    setAppliedSearch(searchInput || '');
    setAppliedPromotionId(promotionIdInput || '');
    setAppliedTypeFilter(typeFilterInput || 'All Types');
    setAppliedActiveStatus(
      activeStatusInput !== undefined && activeStatusInput !== null
        ? activeStatusInput
        : null,
    );
    setAppliedStartDate(startDateInput);
    setAppliedEndDate(endDateInput);
    setPage(0);
    fetchPromotions(
      0,
      rowsPerPage,
      searchInput || '',
      typeFilterInput || 'All Types',
      activeStatusInput !== undefined && activeStatusInput !== null
        ? activeStatusInput
        : null,
      startDateInput,
      endDateInput,
      promotionIdInput || '',
    );
  };

  const handleSearch = () => {
    handleApplyFilters();
  };

  const handleFilterReset = () => {
    resetFilter({
      search: '',
      typeFilter: 'All Types',
      activeStatus: null,
      startDate: null,
      endDate: null,
      promotionId: '',
    });
    // Reset applied filter values
    setAppliedSearch('');
    setAppliedPromotionId('');
    setAppliedTypeFilter('All Types');
    setAppliedActiveStatus(null);
    setAppliedStartDate(null);
    setAppliedEndDate(null);
    setPage(0);
    // Fetch with reset values
    fetchPromotions(0, rowsPerPage, '', 'All Types', null, null, null);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleChangePage = (event, newPage) => {
    // When page changes, fetch that page from the API with applied filters
    fetchPromotions(
      newPage,
      rowsPerPage,
      appliedSearch,
      appliedTypeFilter,
      appliedActiveStatus,
      appliedStartDate,
      appliedEndDate,
      appliedPromotionId,
    );
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    // Reset to first page when page size changes, with applied filters
    fetchPromotions(
      0,
      newRowsPerPage,
      appliedSearch,
      appliedTypeFilter,
      appliedActiveStatus,
      appliedStartDate,
      appliedEndDate,
      appliedPromotionId,
    );
  };

  const validateInputs = () => {
    let valid = true;
    const newErrors = {
      title: '',
      code: '',
      type: '',
      discountValue: '',
      minOrderValue: '',
      usageLimit: '',
      startDate: '',
      endDate: '',
    };

    if (!newPromotion.code.trim()) {
      newErrors.code = 'Code is required.';
      valid = false;
    }

    if (!newPromotion.type.trim()) {
      newErrors.type = 'Type is required.';
      valid = false;
    }

    if (newPromotion.discountValue <= 0) {
      newErrors.discountValue = 'Discount value must be greater than 0.';
      valid = false;
    }

    if (newPromotion.minOrderValue < 0) {
      newErrors.minOrderValue = 'Min order value cannot be negative.';
      valid = false;
    }

    if (newPromotion.usageLimit <= 0) {
      newErrors.usageLimit = 'Usage limit must be greater than 0.';
      valid = false;
    }

    if (!newPromotion.startDate) {
      newErrors.startDate = 'Start date is required.';
      valid = false;
    }

    if (!newPromotion.endDate) {
      newErrors.endDate = 'End date is required.';
      valid = false;
    }

    if (newPromotion.startDate && newPromotion.endDate) {
      const start = dayjs(newPromotion.startDate);
      const end = dayjs(newPromotion.endDate);
      if (!start.isValid()) {
        newErrors.startDate = 'Start date is invalid.';
        valid = false;
      }
      if (!end.isValid()) {
        newErrors.endDate = 'End date is invalid.';
        valid = false;
      }
      if (start.isValid() && end.isValid()) {
        if (!start.startOf('day').isBefore(end.startOf('day'))) {
          newErrors.startDate = 'Start date must be before end date.';
          newErrors.endDate = 'End date must be after start date.';
          valid = false;
        }
        if (end.startOf('day').isBefore(dayjs().startOf('day'))) {
          newErrors.endDate = 'End date cannot be in the past.';
          valid = false;
        }
        if (
          !isEditMode &&
          start.startOf('day').isBefore(dayjs().startOf('day'))
        ) {
          newErrors.startDate = 'Start date cannot be in the past.';
          valid = false;
        }
      }
    }

    setErrors(newErrors);
    return valid;
  };

  const handleModalOpen = (editMode = false) => {
    setIsEditMode(editMode);
    if (editMode && selectedPromotion) {
      setNewPromotion({
        title: selectedPromotion.title || '',
        code: selectedPromotion.code || '',
        type: selectedPromotion.type || '',
        description: selectedPromotion.description || '',
        discountType: selectedPromotion.discountType || 'percenptage',
        discountValue: selectedPromotion.discountValue || 0,
        maxDiscount: selectedPromotion.maxDiscount || 0,
        minOrderValue: selectedPromotion.minOrderValue || 0,
        startDate: selectedPromotion.startDate
          ? dayjs(selectedPromotion.startDate)
          : null,
        endDate: selectedPromotion.endDate
          ? dayjs(selectedPromotion.endDate)
          : null,
        usageLimit: selectedPromotion.usageLimit || '',
        dailyLimit: selectedPromotion.dailyLimit || '',
        userMaxLimit: selectedPromotion.userMaxLimit || '',
        appliesToAll:
          selectedPromotion.appliesToAll !== undefined
            ? selectedPromotion.appliesToAll
            : true,
        newUserOnly: selectedPromotion.newUserOnly || false,
        targetedUserIds: selectedPromotion.targetedUserId || '',
        image: selectedPromotion.image || null,
        active:
          selectedPromotion.active !== undefined
            ? selectedPromotion.active
            : true,
      });
    } else {
      setNewPromotion({
        title: '',
        code: '',
        type: '',
        description: '',
        discountType: 'percentage',
        discountValue: 0,
        maxDiscount: 0,
        minOrderValue: 0,
        startDate: null,
        endDate: null,
        usageLimit: '',
        dailyLimit: '',
        userMaxLimit: '',
        appliesToAll: true,
        newUserOnly: false,
        targetedUserIds: '',
        image: null,
        active: true,
      });
    }
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setNewPromotion({
      title: '',
      code: '',
      type: '',
      description: '',
      discountType: 'percentage',
      discountValue: '',
      maxDiscount: '',
      minOrderValue: '',
      startDate: null,
      endDate: null,
      usageLimit: '',
      dailyLimit: '',
      userMaxLimit: '',
      appliesToAll: true,
      newUserOnly: false,
      targetedUserIds: '',
      image: null,
      active: true,
    });
    setErrors({
      title: '',
      code: '',
      type: '',
      discountValue: '',
      minOrderValue: '',
      usageLimit: '',
      startDate: '',
      endDate: '',
    });
    // setSelectedPromotion(null);
  };

  // Fetch promotions with server-side pagination and filtering
  const fetchPromotions = async (
    pageIndex = page,
    pageSize = rowsPerPage,
    searchParam = appliedSearch,
    typeFilterParam = appliedTypeFilter,
    activeStatusParam = appliedActiveStatus,
    startDateParam = appliedStartDate,
    endDateParam = appliedEndDate,
    promotionIdParam = appliedPromotionId,
  ) => {
    setLoading(true);
    try {
      // API expects 1-based page number; UI uses 0-based index
      const apiPage = pageIndex + 1;
      const res = await promotionService.fetchPromotions(
        apiPage,
        pageSize,
        searchParam || '',
        typeFilterParam || '',
        activeStatusParam,
        startDateParam,
        endDateParam,
        promotionIdParam || '',
      );
      //  console.log("API Response:", res);
      //  console.log("Response data:", res.data);
      setApiResponse(res.data);
      setPage(pageIndex);
      setRowsPerPage(pageSize);
    } catch (err) {
      toast.error('Error fetching promotions');
    } finally {
      setLoading(false);
    }
  };

  const handleAddPromotion = () => {
    if (validateInputs()) {
      setLoading(true);

      // Format the data similar to handleCreatePromotion
      const promotionData = {
        title: newPromotion.title || '',
        promoCode: newPromotion.code || '',
        type: newPromotion.type || '',
        discountType: newPromotion.discountType || '',
        discountValue:
          newPromotion.discountValue === '' ||
          newPromotion.discountValue == null
            ? 0
            : Number(newPromotion.discountValue),
        minOrderValue:
          newPromotion.minOrderValue === '' ||
          newPromotion.minOrderValue == null
            ? 0
            : Number(newPromotion.minOrderValue),
        usageLimit:
          newPromotion.usageLimit === '' || newPromotion.usageLimit == null
            ? 0
            : Number(newPromotion.usageLimit),
        maxLimit:
          newPromotion.userMaxLimit === '' || newPromotion.userMaxLimit == null
            ? 0
            : Number(newPromotion.userMaxLimit),
        promoDayLimit:
          newPromotion.dailyLimit === '' || newPromotion.dailyLimit == null
            ? 0
            : Number(newPromotion.dailyLimit),

            startDate: newPromotion.startDate
              ? dayjs(newPromotion.startDate).startOf('day').toISOString()
              : '',
            
            endDate: newPromotion.endDate
              ? dayjs(newPromotion.endDate).endOf('day').toISOString()
              : '',
        maxDiscount:
          newPromotion.maxDiscount === '' || newPromotion.maxDiscount == null
            ? 0
            : Number(newPromotion.maxDiscount),
        description: newPromotion.description || '',
        userIds:
          newPromotion.type === 'SPU' ? newPromotion.targetedUserIds : '',
        active: newPromotion.active !== undefined ? newPromotion.active : true,
      };

      // Convert to FormData for multipart/form-data
      const formData = new FormData();
      Object.keys(promotionData).forEach((key) => {
        const value = promotionData[key];
        if (value !== null && value !== undefined) {
          if (Array.isArray(value)) {
            formData.append(key, JSON.stringify(value));
          } else {
            formData.append(key, value);
          }
        }
      });

      // Handle image: only append if it's a File object (binary)
      if (newPromotion.image && newPromotion.image instanceof File) {
        formData.append('imagePath', newPromotion.image);
      }

      promotionService
        .createNewPromotion(formData)
        .then(() => {
          toast.success('Promotion added successfully!');
          return fetchPromotions();
        })
        .then(() => handleModalClose())
        .catch((error) => {
          toast.error(
            error.response?.data?.message || 'Error adding promotion',
          );
        })
        .finally(() => setLoading(false));
    }
  };
  const handleCreatePromotion = (data) => {
    setLoading(true);
    const promotionData = {
      title: data.title || '',
      promoCode: data.code || '',
      type: data.type || '',
      discountType: data.discountType || '',
      discountValue:
        data.discountValue === '' || data.discountValue == null
          ? 0
          : Number(data.discountValue),
      minOrderValue:
        data.minOrderValue === '' || data.minOrderValue == null
          ? 0
          : Number(data.minOrderValue),
      usageLimit:
        data.usageLimit === '' || data.usageLimit == null
          ? 0
          : Number(data.usageLimit),
      maxLimit:
        data.userMaxLimit === '' || data.userMaxLimit == null
          ? 0
          : Number(data.userMaxLimit),
      promoDayLimit:
        data.dailyLimit === '' || data.dailyLimit == null
          ? 0
          : Number(data.dailyLimit),
          startDate: data.startDate
          ? dayjs(data.startDate).startOf('day').toISOString()
          : '',
        
        endDate: data.endDate
          ? dayjs(data.endDate).endOf('day').toISOString()
          : '',
      maxDiscount:
        data.maxDiscount === '' || data.maxDiscount == null
          ? 0
          : Number(data.maxDiscount),
      description: data.description || '',
      userIds: data.type === 'SPU' ? data.targetedUserIds : '',
      active:
        promotionTitleType === 'Edit' ? data.active : true,
    };
    // Convert to FormData for multipart/form-data
    const formData = new FormData();
    Object.keys(promotionData).forEach((key) => {
      const value = promotionData[key];
      if (value !== null && value !== undefined) {
        if (Array.isArray(value)) {
          // For arrays, append as JSON string
          formData.append(key, JSON.stringify(value));
        } else {
          formData.append(key, value);
        }
      }
    });

    // Handle image: only append if it's a File object (binary)
    // Check if image is a File object (not string URL)
    if (data.image) {
      // Verify it's actually a File object
      if (data.image instanceof File) {
        formData.append('imagePath', data.image);
      } else if (
        typeof data.image === 'object' &&
        data.image.constructor === File
      ) {
        // Fallback check for File objects
        formData.append('imagePath', data.image);
      }
      // If it's a string (URL), we don't append it (only binary allowed)
    }

    // Convert to FormData for multipart/form-data
    // FIX: Remove redeclaration of formData (move declaration above this code block if already declared).
    Object.keys(promotionData).forEach((key) => {
      const value = promotionData[key];
      if (value !== null && value !== undefined) {
        if (Array.isArray(value)) {
          // For arrays, append as JSON string
          formData.append(key, JSON.stringify(value));
        } else {
          formData.append(key, value);
        }
      }
    });

    // Handle image: only append if it's a File object (binary)
    // Check if image is a File object (not string URL)
    if (data.image) {
      // Verify it's actually a File object
      if (data.image instanceof File) {
        formData.append('imagePath', data.image);
      } else if (
        typeof data.image === 'object' &&
        data.image.constructor === File
      ) {
        // Fallback check for File objects
        formData.append('imagePath', data.image);
      }
      // If it's a string (URL), we don't append it (only binary allowed)
    }

    if (promotionTitleType !== 'Edit') {
      promotionService
        .createNewPromotion(formData)
        .then((res) => {
          toast.success('Promotion created successfully!');
          setLoading(false);
          resetCreatePromotion();
          setCreatePromotionModalOpen(false);
          return fetchPromotions();
        })
        .catch((error) => {
          setLoading(false);
          toast.error(
            error.response?.data?.message || 'Error creating promotion',
          );
        });
    } else {
      promotionService
        .updatePromotion(selectedPromotion.promotionId, formData)
        .then((res) => {
          toast.success('Promotion Updated successfully!');
          setLoading(false);
          resetCreatePromotion();
          setCreatePromotionModalOpen(false);
          return fetchPromotions();
        })
        .catch((error) => {
          setLoading(false);
          toast.error(
            error.response?.data?.message || 'Error updating promotion',
          );
        });
    }
  };
  const handleSoftDelete = (id) => {
    setLoading(true);
    promotionService
      .softDeletePromotion(id)
      .then(() => {
        toast.success('Promotion soft deleted successfully!');
        setLoading(false);
        return fetchPromotions();
      })
      .catch((error) => {
        toast.error('Error soft deleting promotion', error);
        setLoading(false);
      })
      .finally(() => setLoading(false));
  };

  // soft delete
  const [softDeleteClickOpen, setSoftDeleteClickOpen] = useState(false);
  const handleSoftDeleteClickOpen = () => {
    setSoftDeleteClickOpen(true);
  };
  const handleSoftDeleteClickClose = () => {
    setSoftDeleteClickOpen(false);
  };
  const handleSoftDeleteClickSubmit = () => {
    handleSoftDelete(selectedPromotion.promotionId);
    handleSoftDeleteClickClose();
  };

  const handleCreatePromotionModalClose = () => {
    resetCreatePromotion();
    setCreatePromotionModalOpen(false);
  };
  const handleEditPromotion = () => {
    if (validateInputs()) {
      setLoading(true);

      // Format the data to match backend API expectations (same structure as handleCreatePromotion)
      const promotionData = {
        title: newPromotion.title || '',
        promoCode: newPromotion.code || '',
        type: newPromotion.type || '',
        discountType: newPromotion.discountType || '',
        discountValue:
          newPromotion.discountValue === '' ||
          newPromotion.discountValue == null
            ? 0
            : Number(newPromotion.discountValue),
        minOrderValue:
          newPromotion.minOrderValue === '' ||
          newPromotion.minOrderValue == null
            ? 0
            : Number(newPromotion.minOrderValue),
        usageLimit:
          newPromotion.usageLimit === '' || newPromotion.usageLimit == null
            ? 0
            : Number(newPromotion.usageLimit),
        maxLimit:
          newPromotion.userMaxLimit === '' || newPromotion.userMaxLimit == null
            ? 0
            : Number(newPromotion.userMaxLimit),
        promoDayLimit:
          newPromotion.dailyLimit === '' || newPromotion.dailyLimit == null
            ? 0
            : Number(newPromotion.dailyLimit),

            startDate: newPromotion.startDate
              ? dayjs(newPromotion.startDate).startOf('day').toISOString()
              : '',
            
            endDate: newPromotion.endDate
              ? dayjs(newPromotion.endDate).endOf('day').toISOString()
              : '',
        maxDiscount:
          newPromotion.maxDiscount === '' || newPromotion.maxDiscount == null
            ? 0
            : Number(newPromotion.maxDiscount),
        description: newPromotion.description || '',
        userIds:
          newPromotion.type === 'SPU' && newPromotion.targetedUserIds
            ? newPromotion.targetedUserIds
            : '',
        active: newPromotion.active !== undefined ? newPromotion.active : true,
      };

      // Convert to FormData for multipart/form-data
      const formData = new FormData();
      Object.keys(promotionData).forEach((key) => {
        const value = promotionData[key];
        if (value !== null && value !== undefined) {
          if (Array.isArray(value)) {
            // For arrays, append each item separately or as JSON string
            formData.append(key, JSON.stringify(value));
          } else {
            formData.append(key, value);
          }
        }
      });

      // Handle image: only append if it's a File object (binary)
      if (newPromotion.image && newPromotion.image instanceof File) {
        formData.append('imagePath', newPromotion.image);
      }

      promotionService
        .updatePromotion(selectedPromotion.promotionId, formData)
        .then(() => {
          toast.success('Promotion updated successfully!');
          return fetchPromotions();
        })
        .then(() => handleModalClose())
        .catch((error) => {
          toast.error(
            error.response?.data?.message || 'Error updating promotion',
          );
        })
        .finally(() => setLoading(false));
    }
  };

  const handleDelete = () => {
    setLoading(true);
    promotionService
      .deletePromotion(selectedPromotion.promotionId)
      .then(() => {
        toast.success('Deleted successfully');
        return fetchPromotions();
      })
      .then(() => handleMenuClose())
      .catch((error) => {
        toast.error('Error deleting promotion', error);
      })
      .finally(() => setLoading(false));
  };

  const [promotionTitleType, setPromotionTitleType] = useState('Create New');

  // Bind start/end dates to form when edit modal opens (after form has mounted)
  useEffect(() => {
    if (
      !createPromotionModalOpen ||
      promotionTitleType !== 'Edit' ||
      !selectedPromotion?.promotionId
    ) {
      return;
    }
    const s = toDayjsOrNull(selectedPromotion.startDate);
    const e = toDayjsOrNull(selectedPromotion.endDate);
    setCreatePromotionValue('startDate', s);
    setCreatePromotionValue('endDate', e);
  }, [
    createPromotionModalOpen,
    promotionTitleType,
    selectedPromotion?.promotionId,
    selectedPromotion?.startDate,
    selectedPromotion?.endDate,
    setCreatePromotionValue,
  ]);

  const handleEditModalOpen = (eventOrRow, maybeRow) => {
    let event = null;
    let row = null;

    if (eventOrRow && typeof eventOrRow.stopPropagation === 'function') {
      event = eventOrRow;
      row = maybeRow;
      event.stopPropagation();
    } else {
      row = eventOrRow;
    }

    if (!row) {
      logger.warn('handleEditModalOpen: no row provided');
      return;
    }

    const promo = { ...row };

    setSelectedPromotion(promo);
    setPromotionTitleType('Edit');
    setCreatePromotionModalOpen(true);

    const s = toDayjsOrNull(promo.startDate);
    const e = toDayjsOrNull(promo.endDate);

    // Patch form fields individually
    setCreatePromotionValue('title', promo.title ?? '');
    setCreatePromotionValue('code', promo.promoCode ?? '');
    setCreatePromotionValue('type', promo.type ?? '');
    setCreatePromotionValue('description', promo.description ?? '');
    setCreatePromotionValue('discountType', promo.discountType ?? '');
    setCreatePromotionValue('discountValue', promo.discountValue ?? '');
    setCreatePromotionValue('maxDiscount', promo.maxDiscount ?? '');
    setCreatePromotionValue('minOrderValue', promo.minOrderValue ?? '');
    setCreatePromotionValue(
      'usageLimit',
      promo.usageLimit != null ? String(promo.usageLimit) : '',
    );
    setCreatePromotionValue(
      'dailyLimit',
      promo.promoDayLimit != null ? String(promo.promoDayLimit) : '',
    );
    setCreatePromotionValue(
      'userMaxLimit',
      promo.maxLimit != null ? String(promo.maxLimit) : '',
    );
    setCreatePromotionValue(
      'targetedUserIds',
      Array.isArray(promo.assignedUserIds)
        ? promo.assignedUserIds.map(String).join(', ')
        : promo.assignedUserIds?.trim() ?? '',
    );
    setCreatePromotionValue('image', promo.imagePath ?? '');
    setCreatePromotionValue('active', !!promo.active);
    // Set dates after a tick so form/DatePicker has mounted (fixes binding in UI)
    setTimeout(() => {
      setCreatePromotionValue('startDate', s);
      setCreatePromotionValue('endDate', e);
    }, 0);
  };

  const [viewPromotionModalOpen, setViewPromotionModalOpen] = useState(false);
  const handleViewPromotionModalClose = () => {
    setViewPromotionModalOpen(false);
  };
  const handleViewModalOpen = async (row) => {
    setPromotionTitleType('View');
    setViewPromotionModalOpen(true);

    if (!row?.promotionId) {
      return;
    }

    try {
      const res = await promotionService.getPromotionById(row.promotionId);
      const fetchedPromotion = res?.data?.data?.promotion;

      if (fetchedPromotion) {
        setSelectedPromotion((prevPromotion) => ({
          ...(prevPromotion || {}),
          ...row,
          ...fetchedPromotion,
        }));
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || 'Failed to fetch promotion details',
      );
    }
  };
  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <span className="global_loader"></span>
      </div>
    );
  }

  return (
    <>
      <Card
        sx={{
          boxShadow: 2,
          borderRadius: 2,
          padding: 1,
          margin: 2,
          marginLeft: 4,
        }}
      >
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingLeft: 0,
              paddingTop: 2,
              paddingBottom: 0,
            }}
          >
            <h3>Promotions</h3>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                setCreatePromotionModalOpen(true);
                setPromotionTitleType('Create New');
              }}
              sx={{
                bgcolor: colors.swiftcarry[100],
                '&:hover': {
                  bgcolor: colors.swiftcarry[100],
                },
                height: '40px',
                width: '250px',
              }}
            >
              <Add sx={{ color: 'white', mr: 1 }} />
              Create New Promotion
            </Button>
          </Box>

          <Modal
            open={createPromotionModalOpen}
            onClose={handleCreatePromotionModalClose}
          >
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: { xs: '95%', sm: '90%', md: '800px' },
                maxHeight: '90vh',
                bgcolor: 'background.paper',
                boxShadow: 24,
                borderRadius: 2,
                overflow: 'auto',
              }}
            >
              {/* Header */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  p: 3,
                  borderBottom: '1px solid #e0e0e0',
                }}
              >
                <Typography
                  variant="h5"
                  component="h2"
                  sx={{ fontWeight: 600 }}
                >
                  {promotionTitleType} Promotion
                </Typography>
                <IconButton
                  onClick={handleCreatePromotionModalClose}
                  sx={{ color: '#666', width: '40px', height: '40px' }}
                >
                  <Close />
                </IconButton>
              </Box>

              <Box
                component="form"
                onSubmit={handleCreatePromotionSubmit(handleCreatePromotion)}
                sx={{ p: 3 }}
              >
                {/* Basic Information */}
                <Typography
                  variant="h6"
                  sx={{ mb: 2, fontWeight: 600, color: '#333' }}
                >
                  Basic Information
                </Typography>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={12}>
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Title{promotionTitleType !== 'Edit' && '*'}
                      </Typography>
                      <Tooltip title="Enter the promotion title">
                        <Info sx={{ fontSize: 16, color: '#999', ml: 0.5 }} />
                      </Tooltip>
                    </Box>
                    <Controller
                      name="title"
                      control={createPromotionControl}
                      rules={
                        promotionTitleType === 'Edit'
                          ? {}
                          : {
                            required: 'Title is required',
                            minLength: {
                              value: 3,
                              message: 'Title must be at least 3 characters',
                            },
                          }
                      }
                      render={({ field }) => (
                        <TextField
                          {...field}
                          variant="outlined"
                          fullWidth
                          size="small"
                          error={!!createPromotionFormErrors.title}
                          helperText={createPromotionFormErrors.title?.message}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              height: '40px',
                              borderRadius: '8px',
                              backgroundColor: 'white',
                              '&.Mui-focused fieldset': {
                                borderColor: colors.swiftcarry[100],
                                borderWidth: '2px',
                              },
                              '&:hover fieldset': {
                                borderColor: colors.swiftcarry[100],
                              },
                            },
                            '& .MuiInputBase-input': {
                              fontSize: '14px',
                              padding: '10px 14px',
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Promo Code{promotionTitleType !== 'Edit' && '*'}
                      </Typography>
                      <Tooltip title="Enter the promotion code">
                        <Info sx={{ fontSize: 16, color: '#999', ml: 0.5 }} />
                      </Tooltip>
                    </Box>
                    <Controller
                      name="code"
                      control={createPromotionControl}
                      rules={
                        promotionTitleType === 'Edit'
                          ? {}
                          : {
                            required: 'Promo code is required',
                            pattern: {
                              value: /^[A-Z0-9]+$/,
                              message:
                                  'Promo code must contain only uppercase letters and numbers',
                            },
                            minLength: {
                              value: 6,
                              message:
                                  'Promo code must be at least 6 characters',
                            },
                          }
                      }
                      render={({ field }) => (
                        <TextField
                          {...field}
                          variant="outlined"
                          fullWidth
                          size="small"
                          disabled={promotionTitleType === 'Edit'}
                          error={!!createPromotionFormErrors.code}
                          helperText={createPromotionFormErrors.code?.message}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              height: '40px',
                              borderRadius: '8px',
                              backgroundColor: 'white',
                              '&.Mui-focused fieldset': {
                                borderColor: colors.swiftcarry[100],
                                borderWidth: '2px',
                              },
                              '&:hover fieldset': {
                                borderColor: colors.swiftcarry[100],
                              },
                            },
                            '& .MuiInputBase-input': {
                              fontSize: '14px',
                              padding: '10px 14px',
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Promotion Type{promotionTitleType !== 'Edit' && '*'}
                      </Typography>
                      <Tooltip title="Select the promotion type">
                        <Info sx={{ fontSize: 16, color: '#999', ml: 0.5 }} />
                      </Tooltip>
                    </Box>
                    <Controller
                      name="type"
                      control={createPromotionControl}
                      rules={
                        promotionTitleType === 'Edit'
                          ? {}
                          : { required: 'Promotion type is required' }
                      }
                      render={({ field }) => (
                        <FormControl
                          fullWidth
                          size="small"
                          error={!!createPromotionFormErrors.type}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              height: '40px',
                              borderRadius: '8px',
                              backgroundColor: 'white',
                              '&.Mui-focused fieldset': {
                                borderColor: colors.swiftcarry[100],
                                borderWidth: '2px',
                              },
                              '&:hover fieldset': {
                                borderColor: colors.swiftcarry[100],
                              },
                            },
                            '& .MuiInputBase-input': {
                              fontSize: '14px',
                              padding: '10px 14px',
                            },
                          }}
                        >
                          <Select {...field} value={field.value ?? ''}>
                            <DropdownItem value="ALU">All Users</DropdownItem>
                            <DropdownItem value="NEU">New Users</DropdownItem>
                            <DropdownItem value="SPU">
                              Specific Users
                            </DropdownItem>
                          </Select>

                          {createPromotionFormErrors.type && (
                            <FormHelperText>
                              {createPromotionFormErrors.type.message}
                            </FormHelperText>
                          )}
                        </FormControl>
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Description{promotionTitleType !== 'Edit' && '*'}
                      </Typography>
                      <Tooltip title="Enter the description">
                        <Info sx={{ fontSize: 16, color: '#999', ml: 0.5 }} />
                      </Tooltip>
                    </Box>
                    <Controller
                      name="description"
                      control={createPromotionControl}
                      rules={
                        promotionTitleType === 'Edit'
                          ? {}
                          : {
                            maxLength: {
                              value: 500,
                              message:
                                  'Description must not exceed 500 characters',
                            },
                            required: 'Description is required',
                          }
                      }
                      render={({ field }) => (
                        <TextField
                          {...field}
                          multiline
                          rows={2}
                          variant="outlined"
                          fullWidth
                          inputProps={{ maxLength: 500 }}
                          helperText={`${field.value?.length || 0} / 500${
                            createPromotionFormErrors.description
                              ? ` - ${createPromotionFormErrors.description.message}`
                              : ''
                          }`}
                          error={!!createPromotionFormErrors.description}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '8px',
                              backgroundColor: 'white',
                              '&.Mui-focused fieldset': {
                                borderColor: colors.swiftcarry[100],
                                borderWidth: '2px',
                              },
                              '&:hover fieldset': {
                                borderColor: colors.swiftcarry[100],
                              },
                            },
                            '& .MuiInputBase-input': {
                              fontSize: '14px',
                              padding: '10px 14px',
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                {/* Discount Details */}
                <Typography
                  variant="h6"
                  sx={{ mb: 2, fontWeight: 600, color: '#333' }}
                >
                  Discount Details
                </Typography>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Discount Type{promotionTitleType !== 'Edit' && '*'}
                      </Typography>
                      <Tooltip title="Select discount calculation method">
                        <Info sx={{ fontSize: 16, color: '#999', ml: 0.5 }} />
                      </Tooltip>
                    </Box>
                    <Controller
                      name="discountType"
                      control={createPromotionControl}
                      rules={
                        promotionTitleType === 'Edit'
                          ? {}
                          : { required: 'Discount type is required' }
                      }
                      render={({ field }) => (
                        <RadioGroup row {...field}>
                          <FormControlLabel
                            value="p"
                            control={
                              <Radio
                                sx={{
                                  color: colors.swiftcarry[100],
                                  '&.Mui-checked': {
                                    color: colors.swiftcarry[100],
                                  },
                                }}
                              />
                            }
                            label="Percentage"
                          />
                          <FormControlLabel
                            value="f"
                            control={
                              <Radio
                                sx={{
                                  color: colors.swiftcarry[100],
                                  '&.Mui-checked': {
                                    color: colors.swiftcarry[100],
                                  },
                                }}
                              />
                            }
                            label="Flat amount"
                          />
                        </RadioGroup>
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Discount Value{promotionTitleType !== 'Edit' && '*'}
                      </Typography>
                      <Tooltip title="Enter the discount amount or percentage">
                        <Info sx={{ fontSize: 16, color: '#999', ml: 0.5 }} />
                      </Tooltip>
                    </Box>
                    <Controller
                      name="discountValue"
                      control={createPromotionControl}
                      rules={
                        promotionTitleType === 'Edit'
                          ? {}
                          : {
                            required: 'Discount value is required',
                            validate: (value) => {
                              if (value === '' || value == null) {
                                return 'Discount value is required';
                              }
                              const numValue = Number(value);
                              if (isNaN(numValue) || numValue <= 0) {
                                return 'Discount value must be greater than 0';
                              }
                              if (
                                (discountType === 'p' ||
                                    discountType === 'percentage') &&
                                  numValue > 100
                              ) {
                                return 'Percentage discount cannot exceed 100%';
                              }
                              return true;
                            },
                          }
                      }
                      render={({ field }) => (
                        <TextField
                          {...field}
                          value={
                            field.value === '' || field.value == null
                              ? ''
                              : field.value
                          }
                          variant="outlined"
                          fullWidth
                          size="small"
                          type="number"
                          onChange={(e) => {
                            const value = e.target.value;
                            field.onChange(value === '' ? '' : Number(value));
                          }}
                          error={!!createPromotionFormErrors.discountValue}
                          helperText={
                            createPromotionFormErrors.discountValue?.message
                          }
                          InputProps={{
                            endAdornment:
                              discountType === 'p' ||
                              discountType === 'percentage' ? (
                                  <InputAdornment position="end">
                                  %
                                  </InputAdornment>
                                ) : (
                                  <InputAdornment position="end">
                                  ₹
                                  </InputAdornment>
                                ),
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              height: '40px',
                              borderRadius: '8px',
                              backgroundColor: 'white',
                              '&.Mui-focused fieldset': {
                                borderColor: colors.swiftcarry[100],
                                borderWidth: '2px',
                              },
                              '&:hover fieldset': {
                                borderColor: colors.swiftcarry[100],
                              },
                            },
                            '& .MuiInputBase-input': {
                              fontSize: '14px',
                              padding: '10px 14px',
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>
                  {isPercentageDiscount && (
                    <Grid item xs={12} sm={6}>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          Max Discount
                        </Typography>
                        <Tooltip title="Maximum discount amount that can be applied">
                          <Info sx={{ fontSize: 16, color: '#999', ml: 0.5 }} />
                        </Tooltip>
                      </Box>
                      <Controller
                        name="maxDiscount"
                        control={createPromotionControl}
                        rules={
                          promotionTitleType === 'Edit'
                            ? {}
                            : {
                              validate: (value) => {
                                if (value === '' || value == null) {
                                  return true; // Optional field
                                }
                                const numValue = Number(value);
                                if (isNaN(numValue) || numValue < 0) {
                                  return 'Max discount cannot be negative';
                                }
                                return true;
                              },
                            }
                        }
                        render={({ field }) => (
                          <TextField
                            {...field}
                            value={
                              field.value === '' || field.value == null
                                ? ''
                                : field.value
                            }
                            variant="outlined"
                            fullWidth
                            size="small"
                            type="number"
                            onChange={(e) => {
                              const value = e.target.value;
                              field.onChange(value === '' ? '' : Number(value));
                            }}
                            error={!!createPromotionFormErrors.maxDiscount}
                            helperText={
                              createPromotionFormErrors.maxDiscount?.message
                            }
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">₹</InputAdornment>
                              ),
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                height: '40px',
                                borderRadius: '8px',
                                backgroundColor: 'white',
                                '&.Mui-focused fieldset': {
                                  borderColor: colors.swiftcarry[100],
                                  borderWidth: '2px',
                                },
                                '&:hover fieldset': {
                                  borderColor: colors.swiftcarry[100],
                                },
                              },
                              '& .MuiInputBase-input': {
                                fontSize: '14px',
                                padding: '10px 14px',
                              },
                            }}
                          />
                        )}
                      />
                    </Grid>
                  )}
                  <Grid item xs={12} sm={6}>
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Min Order Value{promotionTitleType !== 'Edit' && '*'}
                      </Typography>
                      <Tooltip title="Minimum order value required to apply this promotion">
                        <Info sx={{ fontSize: 16, color: '#999', ml: 0.5 }} />
                      </Tooltip>
                    </Box>
                    <Controller
                      name="minOrderValue"
                      control={createPromotionControl}
                      rules={
                        promotionTitleType === 'Edit'
                          ? {}
                          : {
                            required: 'Min order value is required',
                            validate: (value) => {
                              if (value === '' || value == null) {
                                return 'Min order value is required';
                              }
                              const numValue = Number(value);
                              if (isNaN(numValue) || numValue < 0) {
                                return 'Min order value cannot be negative';
                              }
                              return true;
                            },
                          }
                      }
                      render={({ field }) => (
                        <TextField
                          {...field}
                          value={
                            field.value === '' || field.value == null
                              ? ''
                              : field.value
                          }
                          variant="outlined"
                          fullWidth
                          size="small"
                          type="number"
                          onChange={(e) => {
                            const value = e.target.value;
                            field.onChange(value === '' ? '' : Number(value));
                          }}
                          error={!!createPromotionFormErrors.minOrderValue}
                          helperText={
                            createPromotionFormErrors.minOrderValue?.message
                          }
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">₹</InputAdornment>
                            ),
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              height: '40px',
                              borderRadius: '8px',
                              backgroundColor: 'white',
                              '&.Mui-focused fieldset': {
                                borderColor: colors.swiftcarry[100],
                                borderWidth: '2px',
                              },
                              '&:hover fieldset': {
                                borderColor: colors.swiftcarry[100],
                              },
                            },
                            '& .MuiInputBase-input': {
                              fontSize: '14px',
                              padding: '10px 14px',
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                {/* Validity & Usage Limit */}
                <Typography
                  variant="h6"
                  sx={{ mb: 2, fontWeight: 600, color: '#333' }}
                >
                  Validity & Usage Limit
                </Typography>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={6}>
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Start Date{promotionTitleType !== 'Edit' && '*'}
                      </Typography>
                    </Box>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <Controller
                        name="startDate"
                        control={createPromotionControl}
                        rules={
                          promotionTitleType === 'Edit'
                            ? {}
                            : {
                              required: 'Start date is required',
                              validate: (value) => {
                                if (value && !dayjs(value).isValid()) {
                                  return 'Invalid start date';
                                }
                                const endDateValue =
                                    watchCreatePromotion('endDate');
                                if (
                                  value &&
                                    endDateValue &&
                                    dayjs(endDateValue).isValid() &&
                                    !dayjs(value)
                                      .startOf('day')
                                      .isBefore(
                                        dayjs(endDateValue).startOf('day'),
                                      )
                                ) {
                                  return 'Start date must be before end date';
                                }
                                if (
                                  value &&
                                    dayjs(value)
                                      .startOf('day')
                                      .isBefore(dayjs().startOf('day'))
                                ) {
                                  return 'Start date cannot be in the past';
                                }
                                return true;
                              },
                            }
                        }
                        render={({ field: { value, onChange } }) => (
                          <DatePicker
                            value={value}
                            onChange={(newDate) => onChange(newDate)}
                            format="DD/MM/YYYY"
                            slotProps={{
                              textField: {
                                size: 'small',
                                fullWidth: true,
                                error: !!createPromotionFormErrors.startDate,
                                helperText:
                                  createPromotionFormErrors.startDate?.message,
                              },
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                height: '40px',
                                borderRadius: '8px',
                                backgroundColor: 'white',
                                '&.Mui-focused fieldset': {
                                  borderColor: colors.swiftcarry[100],
                                  borderWidth: '2px',
                                },
                                '&:hover fieldset': {
                                  borderColor: colors.swiftcarry[100],
                                },
                              },
                              '& .MuiInputBase-input': {
                                fontSize: '14px',
                                padding: '10px 14px',
                              },
                            }}
                          />
                        )}
                      />
                    </LocalizationProvider>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        End Date{promotionTitleType !== 'Edit' && '*'}
                      </Typography>
                    </Box>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <Controller
                        name="endDate"
                        control={createPromotionControl}
                        rules={
                          promotionTitleType === 'Edit'
                            ? {}
                            : {
                              required: 'End date is required',
                              validate: (value) => {
                                if (value && !dayjs(value).isValid()) {
                                  return 'Invalid end date';
                                }
                                const startDateValue =
                                    watchCreatePromotion('startDate');
                                if (
                                  value &&
                                    startDateValue &&
                                    dayjs(startDateValue).isValid() &&
                                    !dayjs(value)
                                      .startOf('day')
                                      .isAfter(
                                        dayjs(startDateValue).startOf('day'),
                                      )
                                ) {
                                  return 'End date must be after start date';
                                }
                                if (
                                  value &&
                                    dayjs(value)
                                      .startOf('day')
                                      .isBefore(dayjs().startOf('day'))
                                ) {
                                  return 'End date cannot be in the past';
                                }
                                return true;
                              },
                            }
                        }
                        render={({ field: { value, onChange } }) => (
                          <DatePicker
                            value={value}
                            onChange={(newDate) => onChange(newDate)}
                            format="DD/MM/YYYY"
                            slotProps={{
                              textField: {
                                size: 'small',
                                fullWidth: true,
                                error: !!createPromotionFormErrors.endDate,
                                helperText:
                                  createPromotionFormErrors.endDate?.message,
                              },
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                height: '40px',
                                borderRadius: '8px',
                                backgroundColor: 'white',
                                '&.Mui-focused fieldset': {
                                  borderColor: colors.swiftcarry[100],
                                  borderWidth: '2px',
                                },
                                '&:hover fieldset': {
                                  borderColor: colors.swiftcarry[100],
                                },
                              },
                              '& .MuiInputBase-input': {
                                fontSize: '14px',
                                padding: '10px 14px',
                              },
                            }}
                          />
                        )}
                      />
                    </LocalizationProvider>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Usage Limit{promotionTitleType !== 'Edit' && '*'}
                      </Typography>
                      <Tooltip title="Total number of times this promotion can be used">
                        <Info sx={{ fontSize: 16, color: '#999', ml: 0.5 }} />
                      </Tooltip>
                    </Box>
                    <Controller
                      name="usageLimit"
                      control={createPromotionControl}
                      rules={
                        promotionTitleType === 'Edit'
                          ? {}
                          : {
                            validate: (value) => {
                              if (
                                value &&
                                  value !== '' &&
                                  isNaN(Number(value))
                              ) {
                                return 'Usage limit must be a valid number';
                              }
                              if (
                                value &&
                                  value !== '' &&
                                  Number(value) <= 0
                              ) {
                                return 'Usage limit must be greater than 0';
                              }
                              return true;
                            },
                          }
                      }
                      render={({ field }) => (
                        <TextField
                          {...field}
                          variant="outlined"
                          fullWidth
                          size="small"
                          error={!!createPromotionFormErrors.usageLimit}
                          helperText={
                            createPromotionFormErrors.usageLimit?.message
                          }
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              height: '40px',
                              borderRadius: '8px',
                              backgroundColor: 'white',
                              '&.Mui-focused fieldset': {
                                borderColor: colors.swiftcarry[100],
                                borderWidth: '2px',
                              },
                              '&:hover fieldset': {
                                borderColor: colors.swiftcarry[100],
                              },
                            },
                            '& .MuiInputBase-input': {
                              fontSize: '14px',
                              padding: '10px 14px',
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Daily Limit{promotionTitleType !== 'Edit' && '*'}
                      </Typography>
                      <Tooltip title="Maximum number of times this promotion can be used per day">
                        <Info sx={{ fontSize: 16, color: '#999', ml: 0.5 }} />
                      </Tooltip>
                    </Box>
                    <Controller
                      name="dailyLimit"
                      control={createPromotionControl}
                      rules={
                        promotionTitleType === 'Edit'
                          ? {}
                          : {
                            validate: (value) => {
                              if (
                                value &&
                                  value !== '' &&
                                  isNaN(Number(value))
                              ) {
                                return 'Daily limit must be a valid number';
                              }
                              if (
                                value &&
                                  value !== '' &&
                                  Number(value) <= 0
                              ) {
                                return 'Daily limit must be greater than 0';
                              }
                              return true;
                            },
                          }
                      }
                      render={({ field }) => (
                        <TextField
                          {...field}
                          variant="outlined"
                          fullWidth
                          size="small"
                          error={!!createPromotionFormErrors.dailyLimit}
                          helperText={
                            createPromotionFormErrors.dailyLimit?.message
                          }
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              height: '40px',
                              borderRadius: '8px',
                              backgroundColor: 'white',
                              '&.Mui-focused fieldset': {
                                borderColor: colors.swiftcarry[100],
                                borderWidth: '2px',
                              },
                              '&:hover fieldset': {
                                borderColor: colors.swiftcarry[100],
                              },
                            },
                            '& .MuiInputBase-input': {
                              fontSize: '14px',
                              padding: '10px 14px',
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        User Max Limit{promotionTitleType !== 'Edit' && '*'}
                      </Typography>
                      <Tooltip title="Maximum number of times a single user can use this promotion">
                        <Info sx={{ fontSize: 16, color: '#999', ml: 0.5 }} />
                      </Tooltip>
                    </Box>
                    <Controller
                      name="userMaxLimit"
                      control={createPromotionControl}
                      rules={
                        promotionTitleType === 'Edit'
                          ? {}
                          : {
                            validate: (value) => {
                              if (
                                value &&
                                  value !== '' &&
                                  isNaN(Number(value))
                              ) {
                                return 'User max limit must be a valid number';
                              }
                              if (
                                value &&
                                  value !== '' &&
                                  Number(value) <= 0
                              ) {
                                return 'User max limit must be greater than 0';
                              }
                              return true;
                            },
                          }
                      }
                      render={({ field }) => (
                        <TextField
                          {...field}
                          variant="outlined"
                          fullWidth
                          size="small"
                          error={!!createPromotionFormErrors.userMaxLimit}
                          helperText={
                            createPromotionFormErrors.userMaxLimit?.message
                          }
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              height: '40px',
                              borderRadius: '8px',
                              backgroundColor: 'white',
                              '&.Mui-focused fieldset': {
                                borderColor: colors.swiftcarry[100],
                                borderWidth: '2px',
                              },
                              '&:hover fieldset': {
                                borderColor: colors.swiftcarry[100],
                              },
                            },
                            '& .MuiInputBase-input': {
                              fontSize: '14px',
                              padding: '10px 14px',
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>

                <Grid container spacing={2} sx={{ mb: 3 }}>
                  {promotionType === 'SPU' && (
                    <Grid item xs={12}>
                      {/* Targeting & Conditions */}
                      <Typography
                        variant="h6"
                        sx={{ mb: 2, fontWeight: 600, color: '#333' }}
                      >
                        Targeting Users
                      </Typography>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          Targeted User IDs
                          {promotionTitleType !== 'Edit' && '*'}
                        </Typography>
                        <Tooltip title="Enter the targeting users">
                          <Info sx={{ fontSize: 16, color: '#999', ml: 0.5 }} />
                        </Tooltip>
                      </Box>
                      <Controller
                        name="targetedUserIds"
                        control={createPromotionControl}
                        rules={
                          promotionTitleType === 'Edit'
                            ? {}
                            : {
                              validate: (value) => {
                                if (promotionType === 'SPU') {
                                  if (!value || value.trim() === '') {
                                    return 'Enter user IDs separated by commas';
                                  }
                                }
                                return true;
                              },
                            }
                        }
                        render={({ field }) => (
                          <TextField
                            {...field}
                            multiline
                            rows={3}
                            variant="outlined"
                            fullWidth
                            placeholder="Enter user IDs separated by commas"
                            error={!!createPromotionFormErrors.targetedUserIds}
                            helperText={
                              createPromotionFormErrors.targetedUserIds?.message
                            }
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                                backgroundColor: 'white',
                                '&.Mui-focused fieldset': {
                                  borderColor: colors.swiftcarry[100],
                                  borderWidth: '2px',
                                },
                                '&:hover fieldset': {
                                  borderColor: colors.swiftcarry[100],
                                },
                              },
                              '& .MuiInputBase-input': {
                                fontSize: '14px',
                                padding: '10px 14px',
                              },
                            }}
                          />
                        )}
                      />
                    </Grid>
                  )}
                </Grid>

                <Divider sx={{ my: 3 }} />

                {/* Additional Details */}
                <Typography
                  variant="h6"
                  sx={{ mb: 2, fontWeight: 600, color: '#333' }}
                >
                  Additional Details
                </Typography>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12}>
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Promotion Image{promotionTitleType !== 'Edit' && '*'}
                      </Typography>
                      <Tooltip title="Enter the promotion image">
                        <Info sx={{ fontSize: 16, color: '#999', ml: 0.5 }} />
                      </Tooltip>
                    </Box>
                    <Box
                      sx={{
                        border: `2px dashed ${
                          isDragging ? colors.swiftcarry[100] : '#ccc'
                        }`,
                        borderRadius: 2,
                        p: 4,
                        textAlign: 'center',
                        cursor: 'pointer',
                        backgroundColor: isDragging ? '#f0fdf4' : 'transparent',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          borderColor: colors.swiftcarry[100],
                          backgroundColor: '#f9f9f9',
                        },
                      }}
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setIsDragging(true);
                      }}
                      onDragLeave={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setIsDragging(false);
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setIsDragging(false);

                        const files = e.dataTransfer.files;
                        if (files && files.length > 0) {
                          const file = files[0];
                          // Validate file type
                          if (!file.type.startsWith('image/')) {
                            toast.error('Please upload an image file');
                            return;
                          }
                          // Validate file size (2MB)
                          if (file.size > 2 * 1024 * 1024) {
                            toast.error('Image size must be less than 2MB');
                            return;
                          }
                          setCreatePromotionValue('image', file);
                          toast.success('Image uploaded successfully');
                        }
                      }}
                      onClick={() => {
                        const input = document.createElement('input');
                        input.type = 'file';
                        input.accept = 'image/*';
                        input.onchange = (e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            // Validate file type
                            if (!file.type.startsWith('image/')) {
                              toast.error('Please upload an image file');
                              return;
                            }
                            // Validate file size (2MB)
                            if (file.size > 2 * 1024 * 1024) {
                              toast.error('Image size must be less than 2MB');
                              return;
                            }
                            setCreatePromotionValue('image', file);
                            toast.success('Image uploaded successfully');
                          }
                        };
                        input.click();
                      }}
                    >
                      <CloudUpload
                        sx={{ fontSize: 48, color: '#999', mb: 1 }}
                      />
                      <Typography
                        variant="body2"
                        sx={{ color: '#666', mb: 0.5 }}
                      >
                        Click or drag to upload
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#999' }}>
                        800×400px, max 2MB
                      </Typography>
                      <Controller
                        name="image"
                        control={createPromotionControl}
                        render={({ field: { value } }) =>
                          value && (
                            <Box sx={{ mt: 2 }}>
                              {value instanceof File ? (
                                <>
                                  <Box
                                    component="img"
                                    src={URL.createObjectURL(value)}
                                    alt="Preview"
                                    sx={{
                                      maxWidth: '100%',
                                      maxHeight: '200px',
                                      borderRadius: 1,
                                      mb: 1,
                                      objectFit: 'contain',
                                    }}
                                  />
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: colors.swiftcarry[100],
                                      display: 'block',
                                    }}
                                  >
                                    {value.name}
                                  </Typography>
                                  <Button
                                    size="small"
                                    variant="text"
                                    color="error"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setCreatePromotionValue('image', null);
                                    }}
                                    sx={{ mt: 0.5 }}
                                  >
                                    Remove
                                  </Button>
                                </>
                              ) : typeof value === 'string' && value !== '' ? (
                                <>
                                  <Box
                                    component="img"
                                    src={value}
                                    alt="Preview"
                                    sx={{
                                      maxWidth: '100%',
                                      maxHeight: '200px',
                                      borderRadius: 1,
                                      mb: 1,
                                      objectFit: 'contain',
                                    }}
                                  />
                                  <Button
                                    size="small"
                                    variant="text"
                                    color="error"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setCreatePromotionValue('image', null);
                                    }}
                                    sx={{ mt: 0.5 }}
                                  >
                                    Remove
                                  </Button>
                                </>
                              ) : (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: colors.swiftcarry[100],
                                    display: 'block',
                                    mt: 1,
                                  }}
                                >
                                  {value?.name || value}
                                </Typography>
                              )}
                            </Box>
                          )
                        }
                        rules={
                          promotionTitleType === 'Edit'
                            ? {}
                            : { required: 'Promotion image is required' }
                        }
                        error={!!createPromotionFormErrors.image}
                      />
                    </Box>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                {promotionTitleType === 'Edit' && (
                  <Box sx={{ mb: 3 }}>
                    <Controller
                      name="active"
                      control={createPromotionControl}
                      render={({ field: { value, onChange } }) => (
                        <FormControlLabel
                          control={
                            <Switch
                              checked={value}
                              onChange={onChange}
                              sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': {
                                  color: colors.swiftcarry[100],
                                },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track':
                                  {
                                    backgroundColor: colors.swiftcarry[100],
                                  },
                              }}
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 500 }}
                              >
                                Active Status
                              </Typography>
                              <Tooltip title="Toggle whether this promotion is active right now">
                                <Info
                                  sx={{ fontSize: 16, color: '#999', ml: 0.5 }}
                                />
                              </Tooltip>
                            </Box>
                          }
                        />
                      )}
                    />
                    <Typography
                      variant="caption"
                      sx={{ color: '#666', ml: 4, display: 'block', mt: 0.5 }}
                    >
                      Enable or disable this promotion for customers
                    </Typography>
                  </Box>
                )}

                {/* Action Buttons */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 2,
                    pt: 2,
                  }}
                >
                  <Button
                    type="button"
                    variant="outlined"
                    onClick={handleCreatePromotionModalClose}
                    sx={{
                      width: '100%',
                      borderColor: '#ccc',
                      color: '#666',
                      '&:hover': {
                        borderColor: '#999',
                        backgroundColor: '#f5f5f5',
                      },
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={
                      loading ||
                      (promotionTitleType !== 'Edit' && !areRequiredFieldsValid)
                    }
                    sx={{
                      width: '100%',
                      bgcolor: colors.swiftcarry[100],
                      '&:hover': { bgcolor: colors.swiftcarry[100] },
                      '&:disabled': {
                        bgcolor: '#ccc',
                        color: '#fff',
                      },
                    }}
                  >
                    {promotionTitleType === 'Edit' ? 'Update' : 'Create New'}{' '}
                    Promotion
                  </Button>
                </Box>
              </Box>
            </Box>
          </Modal>

          <Divider />

          <Box
            component="form"
            sx={{
              display: 'flex',
              gap: 2,
              mb: 2,
              alignItems: 'center',
              flexWrap: 'wrap',
              padding: '16px 0px',
            }}
          >
            {/* Search Bar */}
            <Controller
              name="search"
              control={filterControl}
              // rules={{
              //   validate: (value) => {
              //     if (value && value.length > 0 && value.trim().length < 2) {
              //       return "Search must be at least 2 characters";
              //     }
              //     return true;
              //   }
              // }}
              render={({ field }) => (
                <TextField
                  {...field}
                  placeholder="Search by promo code"
                  variant="outlined"
                  size="small"
                  onChange={(e) => {
                    field.onChange(e);
                    handleSearchChange(e);
                  }}
                  error={!!formErrors.search}
                  helperText={formErrors.search?.message}
                  InputProps={{
                    startAdornment: <Search sx={{ color: '#999', mr: 1 }} />,
                  }}
                  sx={{
                    minWidth: 250,
                    flex: 1,
                    '& .MuiOutlinedInput-root': {
                      height: '40px',
                      borderRadius: '8px',
                      backgroundColor: 'white',
                      '&.Mui-focused fieldset': {
                        borderColor: colors.swiftcarry[100],
                        borderWidth: '2px',
                      },
                      '&:hover fieldset': {
                        borderColor: colors.swiftcarry[100],
                      },
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '14px',
                      padding: '10px 14px',
                    },
                  }}
                />
              )}
            />

            {/* Type Filter Dropdown */}
            <Controller
              name="typeFilter"
              control={filterControl}
              render={({ field }) => (
                <FormControl
                  size="small"
                  error={!!formErrors.typeFilter}
                  sx={{
                    minWidth: 150,
                    '& .MuiOutlinedInput-root': {
                      height: '40px',
                      borderRadius: '8px',
                      backgroundColor: 'white',
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: colors.swiftcarry[100],
                        borderWidth: '2px',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: colors.swiftcarry[100],
                      },
                    },
                    '& .MuiSelect-select': {
                      fontSize: '14px',
                      padding: '10px 14px',
                    },
                  }}
                >
                  <Select
                    {...field}
                    displayEmpty
                    sx={{
                      '& .MuiSelect-icon': {
                        color: '#666',
                      },
                    }}
                  >
                    <DropdownItem value="All Types" sx={{ fontSize: '14px' }}>
                      All Types
                    </DropdownItem>
                    <DropdownItem value="ALU">All Users</DropdownItem>
                    <DropdownItem value="NEU">New Users</DropdownItem>
                    <DropdownItem value="SPU">Specific Users</DropdownItem>
                  </Select>
                  {formErrors.typeFilter && (
                    <FormHelperText>
                      {formErrors.typeFilter.message}
                    </FormHelperText>
                  )}
                </FormControl>
              )}
            />

            {/* Date Range + Active Status */}
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Controller
                  name="startDate"
                  control={filterControl}
                  rules={{
                    validate: (value) => {
                      if (value && !dayjs(value).isValid()) {
                        return 'Invalid start date';
                      }
                      const endDateValue = watchFilter('endDate');
                      if (
                        value &&
                        endDateValue &&
                        dayjs(endDateValue).isValid() &&
                        !dayjs(value)
                          .startOf('day')
                          .isBefore(dayjs(endDateValue).startOf('day'))
                      ) {
                        return 'Start date must be before end date';
                      }
                      return true;
                    },
                  }}
                  render={({ field: { value, onChange } }) => (
                    <DatePicker
                      className="promotions-date-picker"
                      value={value}
                      onChange={(newDate) => {
                        onChange(newDate);
                        setPage(0);
                      }}
                      format="DD/MM/YYYY"
                      slotProps={{
                        textField: {
                          placeholder: 'Start Date',
                          size: 'small',
                          error: !!formErrors.startDate,
                          helperText: formErrors.startDate?.message,
                          sx: {
                            width: 150,
                            '& .MuiOutlinedInput-root': {
                              height: '40px',
                              borderRadius: '8px',
                              backgroundColor: 'white',
                              '&.Mui-focused fieldset': {
                                borderColor: colors.swiftcarry[100],
                                borderWidth: '2px',
                              },
                              '&:hover fieldset': {
                                borderColor: colors.swiftcarry[100],
                              },
                            },
                            '& .MuiInputBase-input': {
                              fontSize: '14px',
                              padding: '10px 14px',
                            },
                          },
                        },
                      }}
                    />
                  )}
                />

                <Typography sx={{ color: '#666', mx: 0.5 }}>—</Typography>

                <Controller
                  name="endDate"
                  control={filterControl}
                  rules={{
                    validate: (value) => {
                      if (value && !dayjs(value).isValid()) {
                        return 'Invalid end date';
                      }
                      const startDateValue = watchFilter('startDate');
                      if (
                        value &&
                        startDateValue &&
                        dayjs(startDateValue).isValid() &&
                        !dayjs(value)
                          .startOf('day')
                          .isAfter(dayjs(startDateValue).startOf('day'))
                      ) {
                        return 'End date must be after start date';
                      }
                      return true;
                    },
                  }}
                  render={({ field: { value, onChange } }) => (
                    <DatePicker
                      className="promotions-date-picker"
                      value={value}
                      onChange={(newDate) => {
                        onChange(newDate);
                        setPage(0);
                      }}
                      format="DD/MM/YYYY"
                      slotProps={{
                        textField: {
                          placeholder: 'End Date',
                          size: 'small',
                          error: !!formErrors.endDate,
                          helperText: formErrors.endDate?.message,
                          sx: {
                            width: 150,
                            '& .MuiOutlinedInput-root': {
                              height: '40px',
                              borderRadius: '8px',
                              backgroundColor: 'white',
                              '&.Mui-focused fieldset': {
                                borderColor: colors.swiftcarry[100],
                                borderWidth: '2px',
                              },
                              '&:hover fieldset': {
                                borderColor: colors.swiftcarry[100],
                              },
                            },
                            '& .MuiInputBase-input': {
                              fontSize: '14px',
                              padding: '10px 14px',
                            },
                          },
                        },
                        paper: {
                          sx: {
                            '& .MuiPickersCalendarHeader-root': {
                              '& .MuiPickersArrowSwitcher-root': {
                                '& .MuiIconButton-root': {
                                  minWidth: '40px',
                                  width: '40px',
                                  height: '40px',
                                  padding: '8px',
                                  margin: '0 4px',
                                },
                              },
                            },
                          },
                        },
                      }}
                    />
                  )}
                />

                {/* Active Status Toggle */}
                <Controller
                  name="activeStatus"
                  control={filterControl}
                  render={({ field: { value, onChange } }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={value}
                          onChange={onChange}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: colors.swiftcarry[100],
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track':
                              {
                                backgroundColor: colors.swiftcarry[100],
                              },
                          }}
                        />
                      }
                      label="Active Status"
                      sx={{
                        '& .MuiFormControlLabel-label': {
                          fontSize: '14px',
                          fontWeight: 500,
                          color: '#333',
                          ml: 1,
                          whiteSpace: 'nowrap',
                        },
                      }}
                    />
                  )}
                />
              </Box>
            </LocalizationProvider>

            {/* Search Button */}
            <Button
              type="button"
              variant="contained"
              onClick={handleSearch}
              sx={{
                bgcolor: colors.swiftcarry[100],
                color: 'white',
                height: '40px',
                '&:hover': {
                  bgcolor: colors.swiftcarry[100],
                },
                width: '120px',
              }}
            >
              <Search sx={{ mr: 1, fontSize: '18px' }} />
              Search
            </Button>

            {/* Reset Button */}
            <Button
              type="button"
              variant="outlined"
              onClick={handleFilterReset}
              sx={{
                borderColor: '#ccc',
                color: '#666',
                height: '40px',
                '&:hover': {
                  borderColor: colors.swiftcarry[100],
                  color: colors.swiftcarry[100],
                  backgroundColor: `${colors.swiftcarry[100]}10`,
                },
                width: '120px',
              }}
            >
              <Refresh sx={{ mr: 1, fontSize: '18px' }} />
              Reset
            </Button>
          </Box>
          {/* Table View */}
          {isWidgetView && (
            <TableContainer
              component={Paper}
              sx={{ mt: 2, position: 'relative' }}
            >
              <Table sx={{ minWidth: { xs: 800, sm: 650 } }}>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap',
                        minWidth: 180,
                      }}
                    >
                      Promotion ID{' '}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap',
                        minWidth: 180,
                      }}
                    >
                      {' '}
                      Title
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap',
                        minWidth: 120,
                      }}
                    >
                      Promo Code
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap',
                        minWidth: 120,
                      }}
                    >
                      Promotion Type
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap',
                        minWidth: 120,
                      }}
                    >
                      Discount Type
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap',
                        minWidth: 120,
                      }}
                    >
                      Discount Value
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap',
                        minWidth: 120,
                      }}
                    >
                      Max Discount
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap',
                        minWidth: 120,
                      }}
                    >
                      Min Order Value
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap',
                        minWidth: 120,
                      }}
                    >
                      Start Date
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap',
                        minWidth: 120,
                      }}
                    >
                      End Date
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap',
                        minWidth: 120,
                      }}
                    >
                      Active Status
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap',
                        minWidth: 100,
                      }}
                    >
                      Usage Limit
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap',
                        minWidth: 100,
                      }}
                    >
                      Used Count
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap',
                        minWidth: 100,
                      }}
                    >
                      Daily Limit
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap',
                        minWidth: 120,
                      }}
                    >
                      User Max Limit
                    </TableCell>
                  {/*  <TableCell
                      sx={{
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap',
                        minWidth: 100,
                      }}
                    >
                      Applies to All
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap',
                        minWidth: 120,
                      }}
                    >
                      New User Only
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap',
                        minWidth: 150,
                      }}
                    >
                      Targeted User ID
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap',
                        minWidth: 200,
                      }}
                    >
                      Description
                    </TableCell>*/}
                    <TableCell
                      sx={{
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap',
                        minWidth: 100,
                      }}
                    >
                      Image
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap',
                        minWidth: 150,
                      }}
                    >
                      Created On
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap',
                        minWidth: 150,
                      }}
                    >
                      Updated On
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap',
                        minWidth: 120,
                        position: 'sticky',
                        right: 0,
                        backgroundColor: 'white',
                        zIndex: 1,
                        boxShadow: '-2px 0 4px rgba(0,0,0,0.05)',
                      }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {promotionData.length > 0 ? (
                    promotionData.map((row) => (
                      <TableRow
                        key={row.promotionId}
                        sx={{
                          '&:hover': {
                            backgroundColor: '#f9f9f9',
                          },
                        }}
                      >
                        <TableCell>
                          <Box>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600 }}
                            >
                              {row.promotionId || 'N/A'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" sx={{ color: '#666' }}>
                            {row.title || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.5,
                              backgroundColor: `${colors.swiftcarry[100]}15`,
                              border: `2px dashed ${colors.swiftcarry[100]}`,
                              borderRadius: '8px',
                              padding: '6px 12px',
                              fontFamily: 'monospace',
                              fontWeight: 700,
                              fontSize: '14px',
                              color: colors.swiftcarry[100],
                              letterSpacing: '1px',
                              textTransform: 'uppercase',
                            }}
                          >
                            <span style={{ fontSize: '16px' }}>🎟️</span>
                            {row.promoCode || 'N/A'}
                          </Box>
                        </TableCell>
                        <TableCell>{row.type || 'N/A'}</TableCell>
                        <TableCell>
                          {row.discountType === 'p'
                            ? 'Percentage'
                            : row.discountType === 'f'
                              ? 'Flat'
                              : row.discountType || 'N/A'}
                        </TableCell>
                        <TableCell>
                          {row.discountValue != null && row.discountValue !== ''
                            ? row.discountType === 'f'
                              ? `₹${row.discountValue}`
                              : `${row.discountValue}%`
                            : 'N/A'}
                        </TableCell>
                        <TableCell>
                          {row.maxDiscount ? `₹${row.maxDiscount}` : '-'}
                        </TableCell>
                        <TableCell>
                          {row.minOrderValue ? `₹${row.minOrderValue}` : '-'}
                        </TableCell>
                        <TableCell>{formatToIST(row.startDate)}</TableCell>
                        <TableCell>{formatToIST(row.endDate)}</TableCell>
                        <TableCell>
                          <Chip
                            label={row.active ? 'Active' : 'Inactive'}
                            color={row.active ? 'success' : 'default'}
                            size="small"
                            sx={{
                              fontWeight: 500,
                              '& .MuiChip-label': {
                                fontSize: '12px',
                              },
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          {row.usageLimit === -1 || row.usageLimit === null
                            ? 'Unlimited'
                            : row.usageLimit || '-'}
                        </TableCell>
                        <TableCell>{row.usedCount || 0}</TableCell>
                        <TableCell>
                          {row.promoDayLimit === -1 ||
                          row.promoDayLimit === null
                            ? 'Unlimited'
                            : row.promoDayLimit || '-'}
                        </TableCell>
                        <TableCell>{row.maxLimit || '-'}</TableCell>
                        {/* <TableCell>
                          {row.appliesToAll !== undefined
                            ? row.appliesToAll
                              ? 'Yes'
                              : 'No'
                            : '-'}
                        </TableCell>
                        <TableCell>
                          {row.newUserOnly !== undefined
                            ? row.newUserOnly
                              ? 'Yes'
                              : 'No'
                            : '-'}
                        </TableCell>
                        <TableCell>{row.targetedUserId || '-'}</TableCell>
                        <TableCell>
                          <Tooltip title={row.description || '-'}>
                            <Typography
                              variant="body2"
                              sx={{
                                maxWidth: 200,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {row.description || '-'}
                            </Typography>
                          </Tooltip>
                        </TableCell>*/}
                        <TableCell>
                          {row.imagePath || row.image ? (
                            <Box
                              component="img"
                              src={row.imagePath || row.image}
                              alt={
                                row.imagePath
                                  ? row.imagePath.split(/[/\\]/).pop()
                                  : 'N/A'
                              }
                              sx={{
                                width: 40,
                                height: 40,
                                objectFit: 'cover',
                                borderRadius: 1,
                              }}
                            />
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell>
                          {row.createdOn ? formatToIST(row.createdOn) : '-'}
                        </TableCell>
                        <TableCell>
                          {row.updatedOn ? formatToIST(row.updatedOn) : '-'}
                        </TableCell>
                        <TableCell
                          sx={{
                            position: 'sticky',
                            right: 0,
                            backgroundColor: 'white',
                            zIndex: 1,
                            boxShadow: '-2px 0 4px rgba(0,0,0,0.05)',
                          }}
                        >
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Tooltip title="View">
                              <IconButton
                                size="small"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  setSelectedPromotion(row);
                                  handleViewModalOpen(row);
                                }}
                                sx={{
                                  color: colors.swiftcarry[100],
                                  '&:hover': {
                                    backgroundColor: `${colors.swiftcarry[100]}20`,
                                  },
                                  width: '35px',
                                  boxShadow: 'none',
                                }}
                              >
                                <Visibility fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={(e) => handleEditModalOpen(e, row)}
                                sx={{
                                  color: '#1976d2',
                                  '&:hover': {
                                    backgroundColor: '#1976d220',
                                  },
                                  width: '35px',
                                  boxShadow: 'none',
                                }}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  setSelectedPromotion(row);
                                  handleSoftDeleteClickOpen();
                                }}
                                sx={{
                                  color: '#d32f2f',
                                  '&:hover': { backgroundColor: '#d32f2f20' },
                                  width: '35px',
                                  boxShadow: 'none',
                                }}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={22} align="center" sx={{ py: 4 }}>
                        No promotions found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <TablePagination
            rowsPerPageOptions={[5, 10, 50, 100]}
            component="div"
            // Use total from API pagination so it reflects full dataset
            count={pagination.total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Rows per page:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
            }
            sx={{
              width: '100%',
              overflow: 'auto',
              '& .MuiTablePagination-toolbar': {
                paddingLeft: { xs: 1, sm: 2 },
                paddingRight: { xs: 1, sm: 2 },
                flexWrap: 'wrap',
                gap: { xs: 1, sm: 2 },
                minHeight: { xs: '52px', sm: '64px' },
              },
              '& .MuiTablePagination-selectLabel': {
                margin: 0,
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
              },
              '& .MuiTablePagination-displayedRows': {
                margin: 0,
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
              },
              '& .MuiTablePagination-select': {
                marginRight: { xs: 1, sm: 2 },
                marginLeft: { xs: 0.5, sm: 1 },
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
              },
              '& .MuiTablePagination-actions': {
                marginLeft: { xs: 0.5, sm: 1 },
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              },
              '& .MuiIconButton-root': {
                padding: { xs: '4px', sm: '8px' },
              },
              '& .MuiTablePagination-input': {
                marginRight: { xs: 0.5, sm: 1 },
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
              },
              '& .MuiTablePagination-menuItem': {
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
              },
            }}
          />
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={handleMenuClose}
          >
            <DropdownItem
              onClick={() => {
                handleModalOpen(true);
                handleMenuClose();
              }}
            >
              Edit
            </DropdownItem>
            <DropdownItem onClick={handleDelete}>Delete</DropdownItem>
          </Menu>
          <Box sx={{ padding: 4 }}>
            {/* <Grid container spacing={2} alignItems="center" sx={{ marginBottom: 3 }}>
              <Grid item xs={12} sm={9} md={10}>
                <TextField
                  label="Search"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={search}
                  onChange={handleSearchChange}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&.Mui-focused fieldset": {
                        borderColor: colors.swiftcarry[100],
                        color: colors.swiftcarry[100],
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "gray",
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: colors.swiftcarry[100],
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={3} md={2}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => handleModalOpen(false)}
                  fullWidth
                  sx={{
                    bgcolor: colors.swiftcarry[100],
                    "&:hover": {
                      bgcolor: colors.swiftcarry[100],
                    },
                    height: '40px',
                  }}
                >
                  Add
                </Button>
              </Grid>
            </Grid> */}

            {/* <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ color: '#666', fontWeight: 500 }}>
                Total Records: {pagination.total}
              </Typography>
              <IconButton
                onClick={() => setIsWidgetView(!isWidgetView)}
                sx={{
                  backgroundColor: isWidgetView ? colors.swiftcarry[100] : '#f5f5f5',
                  color: isWidgetView ? 'white' : colors.swiftcarry[100],
                  width: '40px',
                  height: '40px',
                  border: `2px solid ${colors.swiftcarry[100]}`,
                  boxShadow: '0 2px 8px rgba(0, 201, 150, 0.2)',
                  '&:hover': {
                    backgroundColor: '#000',
                    color: 'white',
                    transform: 'scale(1.1)',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
                  },
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
              >
                {isWidgetView ? <TableRows /> : <ViewModule />}
              </IconButton>
            </Box> */}

            {/* Widget View */}
            {/* {isWidgetView && (
              <Box sx={{ mb: 3 }}>
                <Grid container spacing={2}>
                {promotionData.length > 0 ? promotionData
                  .map((promotion) => (
                  <Grid item xs={12} sm={6} md={4} key={promotion.promotionId}>
                    <Box
                      sx={{
                        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                        borderRadius: '16px',
                        padding: '20px',
                        border: '1px solid #e0e0e0',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        position: 'relative',
                        overflow: 'hidden',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 8px 25px rgba(0, 201, 150, 0.15)',
                          borderColor: colors.swiftcarry[100]
                        },
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: '4px',
                          background: `linear-gradient(90deg, ${colors.swiftcarry[100]}, ${colors.swiftcarry[200]})`,
                          borderRadius: '16px 16px 0 0'
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Box
                          sx={{
                            width: '48px',
                            height: '48px',
                            borderRadius: '50%',
                            background: `linear-gradient(135deg, ${colors.swiftcarry[100]}, ${colors.swiftcarry[200]})`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '18px',
                            mr: 2
                          }}
                        >
                          {promotion.title ? promotion.title.charAt(0).toUpperCase() : 'P'}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '16px', mb: 0.5 }}>
                            {promotion.title || 'N/A'}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#666', fontSize: '12px' }}>
                            Code: {promotion.code}
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ color: '#333', mb: 1, fontSize: '13px' }}>
                          🏷️ Type: {promotion.type}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#333', mb: 1, fontSize: '13px' }}>
                          💰 Discount: {promotion.discountValue}%
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#333', mb: 1, fontSize: '13px' }}>
                          📊 Usage: {promotion.usedCount}/{promotion.usageLimit}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: promotion.active ? '#2e7d32' : '#f57c00',
                            fontWeight: 500,
                            fontSize: '11px',
                            backgroundColor: promotion.active ? '#e8f5e8' : '#fff3e0',
                            padding: '4px 8px',
                            borderRadius: '12px'
                          }}
                        >
                          {promotion.active ? 'Active' : 'Inactive'}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="caption" sx={{ color: '#999', fontSize: '10px' }}>
                            {formatToIST(promotion.createdOn)}
                          </Typography>
                          <IconButton
                            onClick={(event) => handleMenuOpen(event, promotion)}
                            sx={{
                              padding: '4px',
                              backgroundColor: colors.swiftcarry[100],
                              color: 'white',
                              width: '24px',
                              height: '24px',
                              '&:hover': {
                                backgroundColor: '#000',
                                color: 'white',
                                transform: 'scale(1.1)'
                              },
                              transition: 'all 0.2s'
                            }}
                          >
                            <Settings sx={{ width: 14, height: 14 }} />
                          </IconButton>
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                )) : (
                  <Grid item xs={12}>
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="h6" color="textSecondary">
                        No promotions found
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
              </Box>
            )} */}
          </Box>
        </CardContent>
      </Card>

      <Modal open={modalOpen} onClose={handleModalClose}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
            {isEditMode ? 'Edit Promotion' : 'Add Promotion'}
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Title"
                variant="outlined"
                fullWidth
                size="small"
                sx={{ mb: 2 }}
                value={newPromotion.title}
                onChange={(e) => {
                  setNewPromotion({ ...newPromotion, title: e.target.value });
                  setErrors({ ...errors, title: '' });
                }}
                error={!!errors.title}
                helperText={errors.title}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Code"
                variant="outlined"
                fullWidth
                size="small"
                sx={{ mb: 2 }}
                value={newPromotion.code}
                onChange={(e) => {
                  setNewPromotion({ ...newPromotion, code: e.target.value });
                  setErrors({ ...errors, code: '' });
                }}
                error={!!errors.code}
                helperText={errors.code}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Type"
                variant="outlined"
                fullWidth
                size="small"
                sx={{ mb: 2 }}
                value={newPromotion.type}
                onChange={(e) => {
                  setNewPromotion({ ...newPromotion, type: e.target.value });
                  setErrors({ ...errors, type: '' });
                }}
                error={!!errors.type}
                helperText={errors.type}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Discount Value (%)"
                variant="outlined"
                fullWidth
                size="small"
                type="number"
                sx={{ mb: 2 }}
                value={newPromotion.discountValue}
                onChange={(e) => {
                  setNewPromotion({
                    ...newPromotion,
                    discountValue: Number(e.target.value),
                  });
                  setErrors({ ...errors, discountValue: '' });
                }}
                error={!!errors.discountValue}
                helperText={errors.discountValue}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Min Order Value"
                variant="outlined"
                fullWidth
                size="small"
                type="number"
                sx={{ mb: 2 }}
                value={newPromotion.minOrderValue}
                onChange={(e) => {
                  setNewPromotion({
                    ...newPromotion,
                    minOrderValue: Number(e.target.value),
                  });
                  setErrors({ ...errors, minOrderValue: '' });
                }}
                error={!!errors.minOrderValue}
                helperText={errors.minOrderValue}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Usage Limit"
                variant="outlined"
                fullWidth
                size="small"
                type="number"
                sx={{ mb: 2 }}
                value={newPromotion.usageLimit}
                onChange={(e) => {
                  setNewPromotion({
                    ...newPromotion,
                    usageLimit: Number(e.target.value),
                  });
                  setErrors({ ...errors, usageLimit: '' });
                }}
                error={!!errors.usageLimit}
                helperText={errors.usageLimit}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Start Date"
                variant="outlined"
                fullWidth
                size="small"
                type="date"
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 2 }}
                value={newPromotion.startDate}
                onChange={(e) => {
                  setNewPromotion({
                    ...newPromotion,
                    startDate: e.target.value,
                  });
                  setErrors({ ...errors, startDate: '' });
                }}
                error={!!errors.startDate}
                helperText={errors.startDate}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="End Date"
                variant="outlined"
                fullWidth
                size="small"
                type="date"
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 2 }}
                value={newPromotion.endDate}
                onChange={(e) => {
                  setNewPromotion({ ...newPromotion, endDate: e.target.value });
                  setErrors({ ...errors, endDate: '' });
                }}
                error={!!errors.endDate}
                helperText={errors.endDate}
              />
            </Grid>
          </Grid>

          <Box sx={{ textAlign: 'right' }}>
            <Button
              variant="contained"
              onClick={isEditMode ? handleEditPromotion : handleAddPromotion}
              disabled={
                !newPromotion.title ||
                !newPromotion.code ||
                !newPromotion.type ||
                newPromotion.discountValue <= 0 ||
                newPromotion.usageLimit <= 0 ||
                !newPromotion.startDate ||
                !newPromotion.endDate
              }
              sx={{
                bgcolor: colors.swiftcarry[100],
                '&:hover': { bgcolor: colors.swiftcarry[100] },
              }}
            >
              {isEditMode ? 'Save Changes' : 'Add'}
            </Button>
          </Box>
        </Box>
      </Modal>
      {/* <Dialog
        open={softDeleteClickOpen}
        onClose={handleSoftDeleteClickClose}
        maxWidth="sm"
      >
        <DialogTitle>
          Are you sure you want to soft delete this promotion?
        </DialogTitle>

        <DialogContent>
          <DialogContentText>
            This action will soft delete the promotion and move it to the trash
            bin.
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Grid item xs={12} display="flex" justifyContent="flex-end" gap={2}>
            <Button
              onClick={handleSoftDeleteClickClose}
              color="secondary"
              sx={{
                borderColor: colors.swiftcarry[100],
                color: colors.swiftcarry[100],
                "&:hover": {
                  borderColor: colors.swiftcarry[100],
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSoftDeleteClickSubmit}
              color="primary"
              sx={{
                bgcolor: colors.swiftcarry[100],
                color: "white",
                "&:hover": {
                  bgcolor: colors.swiftcarry[100],
                },
              }}
            >
              Delete
            </Button>
          </Grid>
        </DialogActions>
      </Dialog> */}
      <Dialog
        open={softDeleteClickOpen}
        onClose={handleSoftDeleteClickClose}
        aria-labelledby="delete-dialog-title"
        PaperProps={{
          sx: {
            borderRadius: 4.5, // ~36px
            p: { xs: 3, sm: 6 },
            minWidth: 540,
            overflow: 'visible',
            boxShadow: '0 20px 50px rgba(15,23,42,0.12)',
          },
        }}
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(225,225,225,0.85)',
            backdropFilter: 'blur(2px)',
          },
        }}
      >
        <DialogContent
          sx={{
            textAlign: 'center',
            pt: 6,
            pb: 3,
            px: { xs: 3, sm: 6 },
          }}
        >
          {/* Floating icon */}
          <Box
            sx={{
              position: 'absolute',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              top: 0,
              width: 96,
              height: 96,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
            }}
          >
            <Box
              sx={{
                width: 88,
                height: 88,
                borderRadius: '50%',
                background: (theme) =>
                  `radial-gradient(circle at 40% 35%, ${
                    colors.swiftcarry[100]
                  }44 0, transparent 40%),
                 linear-gradient(180deg, ${
    colors.swiftcarry[40] || '#ffecec'
    } 0%, ${colors.swiftcarry[100] || '#ffdddd'} 100%)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: 0,
              }}
            >
              <Avatar
                sx={{
                  bgcolor: 'background.paper',
                  width: 56,
                  height: 56,
                  boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
                }}
              >
                <WarningAmberOutlinedIcon
                  sx={{
                    bgcolor: colors.swiftcarry[100],
                    '&:hover': {
                      bgcolor: colors.swiftcarry[100],
                    },
                  }}
                />
              </Avatar>
            </Box>
          </Box>

          <Typography
            id="delete-dialog-title"
            variant="h5"
            component="h2"
            sx={{ mt: 3, mb: 1, fontWeight: 500 }}
          >
            Delete Promotion
          </Typography>

          <Typography
            variant="body3"
            color="text.secondary"
            sx={{ whiteSpace: 'pre-line', mb: 6, mt: 1 }}
          >
            Are you sure you want to delete this promotion.
          </Typography>

          <DialogActions sx={{ justifyContent: 'center', gap: 3, px: 0 }}>
            <Button
              onClick={handleSoftDeleteClickClose}
              variant="outlined"
              sx={{
                minWidth: 220,
                py: 1.5,
                borderRadius: 2,
                borderColor: 'grey.200',
                color: 'text.secondary',
                fontSize: 16,
                textTransform: 'none',
              }}
            >
              Cancel
            </Button>

            <Button
              onClick={handleSoftDeleteClickSubmit}
              variant="contained"
              sx={{
                bgcolor: colors.swiftcarry[100],
                '&:hover': {
                  bgcolor: colors.swiftcarry[100],
                },
                minWidth: 180,
                py: 1.5,
              }}
            >
              Delete
            </Button>
          </DialogActions>
        </DialogContent>
      </Dialog>

      <Modal
        open={viewPromotionModalOpen}
        onClose={handleViewPromotionModalClose}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '95%', sm: '90%', md: '70%' },
            maxWidth: '900px',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: { xs: 2, sm: 3, md: 4 },
            borderRadius: 2,
            maxHeight: '90vh',
            overflowY: 'auto',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', sm: 'center' },
              flexWrap: 'wrap',
              gap: 1,
              mb: 1,
            }}
          >
            <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
              Promotion Details - #{selectedPromotion.promotionId ?? '—'}
            </Typography>

            <Box
              sx={{
                minWidth: 220,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                flexWrap: 'wrap',
              }}
            >
              <Typography variant="subtitle2">
                <Box component="span" sx={{ color: 'text.secondary' }}>
                  Title:
                </Box>{' '}
                <Box component="span" sx={{ color: 'text.primary', fontWeight: 700 }}>
                  {selectedPromotion.title ?? 'Untitled'}
                </Box>
              </Typography>
              <Typography variant="body2">
                <Box component="span" sx={{ color: 'text.secondary' }}>
                  Code:
                </Box>{' '}
                <Box component="span" sx={{ color: 'text.primary', fontWeight: 700 }}>
                  {selectedPromotion.promoCode ?? '—'}
                </Box>
              </Typography>
              <Chip
                label={
                  <Box component="span">
                    <Box component="span" sx={{ color: 'text.secondary' }}>
                      Type:
                    </Box>{' '}
                    <Box
                      component="span"
                      sx={{ color: 'text.primary', fontWeight: 700 }}
                    >
                      {selectedPromotion.type ?? '—'}
                    </Box>
                  </Box>
                }
                size="small"
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Chip
                icon={
                  selectedPromotion.active ? <CheckCircle /> : <RemoveCircle />
                }
                label={selectedPromotion.active ? 'Active' : 'Inactive'}
                color={selectedPromotion.active ? 'success' : 'default'}
                size="small"
              />
              <IconButton
                onClick={handleViewPromotionModalClose}
                sx={{ color: '#666', width: '40px', height: '40px' }}
              >
                <Close />
              </IconButton>
            </Box>
          </Box>

          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2}>
            {/* Left: image / basic */}
            <Grid item xs={12} sm={5}>
              <Box
                sx={{
                  borderRadius: 2,
                  overflow: 'hidden',
                  bgcolor: 'grey.100',
                  minHeight: { xs: 220, sm: 320 },
                  maxHeight: { xs: 320, sm: 520 },
                  p: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {selectedPromotion.imagePath ? (
                  <Box
                    component="img"
                    src={selectedPromotion.imagePath}
                    alt={selectedPromotion.title}
                    sx={{
                      width: '100%',
                      height: 'auto',
                      maxHeight: { xs: 300, sm: 350 },
                      objectFit: 'contain',
                    }}
                    onError={() => setImageError(true)}
                  />
                ) : (
                  <BrokenImageIcon sx={{ fontSize: 60, color: '#999' }} />
                )}
              </Box>
            </Grid>

            {/* Right: details */}
            <Grid item xs={12} sm={7}>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Typography color="text.secondary" variant="subtitle2">
                    Start Date
                  </Typography>
                  <Typography>
                    {formatToIST(selectedPromotion.startDate)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography color="text.secondary" variant="subtitle2">
                    End Date
                  </Typography>
                  <Typography>
                    {formatToIST(selectedPromotion.endDate)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography color="text.secondary" variant="subtitle2">
                    Discount Type
                  </Typography>
                  <Typography>
                    {selectedPromotion.discountType === 'p' ||
                    selectedPromotion.discountType === 'percentage'
                      ? 'Percentage'
                      : 'Flat'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography color="text.secondary" variant="subtitle2">
                    Discount Value
                  </Typography>
                  <Typography>
                    {selectedPromotion.discountValue
                      ? `${selectedPromotion.discountValue}${
                        selectedPromotion.discountType === 'p' ||
                          selectedPromotion.discountType === 'percentage'
                          ? '%'
                          : '₹'
                      }`
                      : '—'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography color="text.secondary" variant="subtitle2">
                    Max Discount
                  </Typography>
                  <Typography>
                    {selectedPromotion.maxDiscount
                      ? `₹${selectedPromotion.maxDiscount}`
                      : '—'}
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography color="text.secondary" variant="subtitle2">
                    Min Order Value
                  </Typography>
                  <Typography>
                    {selectedPromotion.minOrderValue
                      ? `₹${selectedPromotion.minOrderValue}`
                      : '—'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography color="text.secondary" variant="subtitle2">
                    Usage Limit
                  </Typography>
                  <Typography>{selectedPromotion.usageLimit ?? '—'}</Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography color="text.secondary" variant="subtitle2">
                    Max Limit
                  </Typography>
                  <Typography>{selectedPromotion.maxLimit ?? '—'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography color="text.secondary" variant="subtitle2">
                    Promo Day Limit
                  </Typography>
                  <Typography>
                    {selectedPromotion.promoDayLimit ?? '—'}
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography color="text.secondary" variant="subtitle2">
                    Used Count
                  </Typography>
                  <Typography>{selectedPromotion.usedCount ?? 0}</Typography>
                </Grid>

                {selectedPromotion.type === 'SPU' && (
                  <Grid item xs={6}>
                    <Typography color="text.secondary" variant="subtitle2">
                      Targeted User IDs
                    </Typography>
                    <Typography>
                      {Array.isArray(selectedPromotion.assignedUserIds)
                        ? selectedPromotion.assignedUserIds
                          .map(String)
                          .join(', ')
                        : selectedPromotion.assignedUserIds?.trim() || '—'}
                    </Typography>
                  </Grid>
                )}
                <Grid item xs={6}>
                  <Typography color="text.secondary" variant="subtitle2">
                    Created On
                  </Typography>
                  <Typography>
                    {formatToIST(selectedPromotion.createdOn)}
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography color="text.secondary" variant="subtitle2">
                    Updated On
                  </Typography>
                  <Typography>
                    {selectedPromotion.updatedOn
                      ? formatToIST(selectedPromotion.updatedOn)
                      : '—'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography color="text.secondary" variant="subtitle2">
                    Description
                  </Typography>
                  <Typography>
                    {selectedPromotion.description ?? '—'}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Modal>
      <Modal open={imagePreviewOpen} onClose={handleImagePreviewClose}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 2,
            borderRadius: 2,
            maxWidth: '90vw',
            maxHeight: '90vh',
            outline: 'none',
          }}
        >
          {/* Close button */}
          <IconButton
            onClick={handleImagePreviewClose}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              bgcolor: 'rgba(0,0,0,0.5)',
              color: '#fff',
              '&:hover': {
                bgcolor: 'rgba(0,0,0,0.7)',
              },
            }}
          >
            <Close />
          </IconButton>

          {/* Image */}
          <Box
            component="img"
            src={previewImageSrc}
            alt="Promotion Preview"
            sx={{
              maxWidth: '85vw',
              maxHeight: '85vh',
              objectFit: 'contain',
              borderRadius: 1,
            }}
          />
        </Box>
      </Modal>
    </>
  );
};

export default Promotions;
